import React, { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CreateVideoClickTarget } from '../../../constants/Bi';
import { useTrackCreateVideoClick } from '../../../utils/hooks/bi/useTrackCreateVideoClick';
import { useVideoMaker } from '../../../utils/hooks/useVideoMaker';
import { Orientations } from '../../../utils/hooks/useVideoMaker/videoMaker.types';
import { useVideoMakerTemplates } from '../../../utils/hooks/useVideoMakerTemplates';
import { Button, PopoverMenu, WixIcons } from '../wixCommon';
import { IOuter } from './HeaderActionsBar.types';

export const HeaderActionsBar: FC<IOuter> = ({ activeTab }) => {
  const { formatMessage } = useIntl();

  const { openVideoMaker } = useVideoMaker();
  const { trackVideoMakerCreateVideoClick } = useTrackCreateVideoClick();
  const { getTemplateData, findTemplateByType } = useVideoMakerTemplates();

  const handleCreateNewVideoButtonClick = (orientation: Orientations) => () => {
    const requiredTemplate = findTemplateByType('create-new');
    const templateData = getTemplateData({
      ...requiredTemplate,
      orientation,
    });

    openVideoMaker(templateData);

    trackVideoMakerCreateVideoClick({
      tabName: activeTab,
      target: orientation,
    });
  };

  const handlePopoverButtonClick = useCallback(() => {
    trackVideoMakerCreateVideoClick({
      tabName: activeTab,
      target: CreateVideoClickTarget.CreateNewVideoTop,
    });
  }, [trackVideoMakerCreateVideoClick, activeTab]);

  return (
    <PopoverMenu
      appendTo="parent"
      placement="bottom"
      showArrow
      dataHook="header-actions-bar-popover-menu"
      triggerElement={({
        toggle,
      }: {
        toggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
      }) => (
        <Button
          suffixIcon={<WixIcons.ChevronDown />}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            handlePopoverButtonClick();
            toggle(event);
          }}
          dataHook="create-new-video-button"
        >
          <FormattedMessage id="video-maker-home.main.header.button" />
        </Button>
      )}
    >
      <PopoverMenu.MenuItem
        prefixIcon={<WixIcons.SquareRatio />}
        text={formatMessage({
          id: 'video-maker-home.main.header.tooltip.orientation.square',
        })}
        onClick={handleCreateNewVideoButtonClick(Orientations.Square)}
        dataHook="header-actions-button-square"
      />

      <PopoverMenu.MenuItem
        prefixIcon={<WixIcons.HorizontalRatio />}
        text={formatMessage({
          id: 'video-maker-home.main.header.tooltip.orientation.landscape',
        })}
        onClick={handleCreateNewVideoButtonClick(Orientations.Landscape)}
        dataHook="header-actions-button-landscape"
      />

      <PopoverMenu.MenuItem
        prefixIcon={<WixIcons.VerticalRatio />}
        text={formatMessage({
          id: 'video-maker-home.main.header.tooltip.orientation.portrait',
        })}
        onClick={handleCreateNewVideoButtonClick(Orientations.Portrait)}
        dataHook="header-actions-button-portrait"
      />
    </PopoverMenu>
  );
};
